import { Component, Fragment, State, Watch, getAssetPath, h } from '@stencil/core';
import { Business, Category, Customer, WidgetStyle } from '../../types';
import { getCustomer, loginCustomer } from '../../utils/api';
import { getApiUrl } from '../../utils/enviroment';
import { isBusiness } from '../../utils/types';

interface ColorAccessibility {
  small: string;
  bold: string;
  large: string;
  overall: string;
  contrast: string;
}

@Component({
  tag: 'hemfixarna-demo',
  styleUrl: 'hemfixarna-demo.scss',
  shadow: true,
})
export class MyComponent {
  @State() selectedSlug: string;
  @State() selectedID: string;
  @State() tooltipText: string = 'Kopiera till urklipp';
  @State() tree: Category | null = null;
  @State() customer: Customer | null = null;
  @State() widgetStyle: WidgetStyle = WidgetStyle.standard;
  @State() buttonBg: string = '';
  @State() buttonColor: string = '';
  @State() partner: Business | undefined = undefined;
  @State() colorAccessibility?: ColorAccessibility = undefined;
  @State() loggedIn: boolean = false;
  @State() loginError: boolean = false;
  private debounce?: number = null;

  @Watch('buttonBg')
  @Watch('buttonColor')
  debouncedFunction() {
    if (this.debounce !== null) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }

    this.debounce = window.setTimeout(() => {
      this.checkColorAccessibility();
      this.debounce = null;
    }, 1500);
  }

  async checkColorAccessibility() {
    const defaultTextColor = '#fff';
    const defaultBgColor = '#c84e18';
    const firstColor = this.buttonBg.length ? (this.buttonBg.startsWith('#') ? this.buttonBg : `#${this.buttonBg}`) : defaultBgColor;
    const secondColor = this.buttonColor.length ? (this.buttonColor.startsWith('#') ? this.buttonColor : `#${this.buttonColor}`) : defaultTextColor;
    try {
      const response = await fetch('https://www.aremycolorsaccessible.com/api/are-they', {
        mode: 'cors',
        method: 'POST',
        body: JSON.stringify({ colors: [firstColor, secondColor] }),
      });

      if (!response) {
        throw new Error('Network response was not ok');
      }

      const json = await response.json();
      if (json && (json satisfies ColorAccessibility)) {
        this.colorAccessibility = json;
      }
    } catch (error) {
      console.warn('Error fetching color accessibility:', error);
    }
  }

  getColor(rating: string) {
    switch (rating) {
      case 'AAA':
        return 'green';
      case 'AA':
        return 'orange';
      case 'A':
        return 'yellow';
      case 'Fail':
        return 'red';
      default:
        return 'black';
    }
  }

  async componentWillLoad() {
    const partner = window.location.pathname.replace('/', '');
    if (!isBusiness(partner) && partner) return;
    this.partner = partner ? (partner as Business) : Business.kund;
    const customer = await getCustomer(this.partner);
    if (customer.code !== 'not_found' && customer) {
      this.customer = customer;
      if (!this.customer.password_protected || window.localStorage.getItem(`${this.customer.ID}-loggedin`)) {
        this.loggedIn = true;
      }
    }
  }

  getExample() {
    return `<hemfixarna-component business="${this.partner}"${this.selectedSlug ? ` slug="${this.selectedSlug}" ` : ''}${this.selectedID ? ` id="${this.selectedID}" ` : ''} ${
      this.widgetStyle === WidgetStyle.standard ? '' : `widget-style="${this.widgetStyle}"`
    } ${this.buttonColor.length ? `button-color="${this.buttonColor}"` : ''} ${this.buttonBg.length ? `button-bg="${this.buttonBg}"` : ''}></hemfixarna-component>`;
  }

  copyExample() {
    navigator.clipboard.writeText(this.getExample());
    this.tooltipText = 'Snippet kopierad';
    setTimeout(() => {
      this.tooltipText = 'Kopiera till urklipp';
    }, 2000);
  }

  cdnLink = '<script type="module" src="https://cdn.jsdelivr.net/npm//hemfixarna-web-components@latest/dist/hemfixarna-components/hemfixarna-components.esm.js"></script>';

  copyCdn() {
    navigator.clipboard.writeText(this.cdnLink);
    this.tooltipText = 'Text kopierad';
    setTimeout(() => {
      this.tooltipText = 'Kopiera till urklipp';
    }, 2000);
  }

  copyNpmInstall() {
    navigator.clipboard.writeText('npm i hemfixarna-web-components');
    this.tooltipText = 'Text kopierad';
    setTimeout(() => {
      this.tooltipText = 'Kopiera till urklipp';
    }, 2000);
  }

  showMaleri() {
    const allCategories = this.customer?.categories.map(c => (c.sub_categories ? c.sub_categories : c)).flat();
    return allCategories?.some(c => c.add_painting);
  }

  async handleLogin() {
    const shadowHost = document.querySelector('hemfixarna-demo');
    const shadowRoot = shadowHost.shadowRoot;
    const passwordInput = shadowRoot.querySelector('input[type="password"]');
    const password = (passwordInput as HTMLInputElement).value;

    this.loginError = false;
    try {
      const status = await loginCustomer({ id: this.customer.ID, password: password });
      if (status === 200) {
        this.loggedIn = true;
        window.localStorage.setItem(`${this.customer.ID}-loggedin`, 'true');
      } else {
        this.loginError = true;
      }
    } catch (error) {
      console.log(error);
      this.loginError = true;
    }
  }

  render() {
    const copy = getAssetPath(`./assets/copy.png`);

    return this.customer ? (
      <Fragment>
        {this.loggedIn ? (
          <div class={this.customer?.at_hemfixarna ? 'hemfixarna_hosted' : ''}>
            <div class="hemfixarna_scripts">
              <div onClick={() => this.copyExample()} class="hemfixarna_example">
                <p>{this.getExample()}</p>
                <img src={copy} height={20} />
                <span class="hemfixarna_example--tooltip">{this.tooltipText}</span>
              </div>
              <hemfixarna-component
                business={this.partner}
                buttonBg={this.buttonBg}
                buttonColor={this.buttonColor}
                widgetStyle={this.widgetStyle}
                id={this.selectedID}
              ></hemfixarna-component>
              <div class="hemfixarna_install">
                <div onClick={() => this.copyCdn()} class="hemfixarna_example">
                  <p>{this.cdnLink}</p>
                  <span class="hemfixarna_example--tooltip">{this.tooltipText}</span>
                  <img src={copy} height={20} />
                </div>
              </div>
              <div class="hemfixarna_widgetstyles">
                <h5>Widget styles</h5>
                <div>
                  {Object.values(WidgetStyle).map(style => (
                    <label key={style}>
                      <input type="radio" value={style} checked={this.widgetStyle === style} onChange={() => (this.widgetStyle = style)} />
                      {style}
                    </label>
                  ))}
                </div>
                <div>
                  <div>
                    <h5>Button background color</h5>
                    <input type="text" value={this.buttonBg} onInput={e => (this.buttonBg = (e.target as HTMLInputElement).value)} />
                  </div>
                  <div>
                    <h5>Button text color</h5>
                    <input type="text" value={this.buttonColor} onInput={e => (this.buttonColor = (e.target as HTMLInputElement).value)} />
                  </div>
                </div>
              </div>
              <span>Write an hexa code no # needed</span>
              {this.colorAccessibility ? (
                <div>
                  <h5>Tillgänglighetsrapport</h5>
                  <div>
                    <strong>Liten text:</strong>
                    <span style={{ color: this.getColor(this.colorAccessibility.small) }}>{this.colorAccessibility.small}</span>
                    <br />
                    <strong>Fet text:</strong>
                    <span style={{ color: this.getColor(this.colorAccessibility.bold) }}>{this.colorAccessibility.bold}</span>
                    <br />
                    <strong>Stor text:</strong>
                    <span style={{ color: this.getColor(this.colorAccessibility.large) }}>{this.colorAccessibility.large}</span>
                    <br />
                    <strong>Kontrastförhållande:</strong> {this.colorAccessibility.contrast}
                  </div>
                </div>
              ) : null}
            </div>
            <ul class="hemfixarna_categories">
              <div>
                {this.customer.logo ? (
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <img style={{ maxWidth: '250px', height: 'auto', margin: '0 auto 2rem' }} src={this.customer.logo.url} alt={this.customer.post_title} />
                  </div>
                ) : null}
                {this.showMaleri() ? (
                  <li>
                    <div class="hemfixarna_categories--label hemfixarna_categories--label--big">
                      <div>
                        <p>Måleriverktyget</p>
                        <span>maleri</span>
                      </div>
                      <div>
                        <button onClick={() => navigator.clipboard.writeText('maleri')}>Kopiera ID</button>
                        {this.customer.at_hemfixarna ? (
                          <button onClick={() => navigator.clipboard.writeText(`${getApiUrl()}/partner/${this.customer.post_name}?id=maleri`)}>Kopiera URL</button>
                        ) : (
                          <button onClick={() => (this.selectedID = 'maleri')}>Ladda måleri</button>
                        )}
                      </div>
                    </div>
                  </li>
                ) : null}
                {this.customer.categories.map(c => (
                  <li>
                    <div class="hemfixarna_categories--label hemfixarna_categories--label--big">
                      <div>
                        <p>{c.name}</p>
                        <span>{`c-${c.id}`}</span>
                      </div>
                      <div>
                        <button onClick={() => navigator.clipboard.writeText(`c-${c.id}`)}>Kopiera ID</button>
                        {this.customer.at_hemfixarna ? (
                          <button onClick={() => navigator.clipboard.writeText(`${getApiUrl()}/partner/${this.customer.post_name}?id=c-${c.id}`)}>Kopiera URL</button>
                        ) : (
                          <button onClick={() => (this.selectedID = `c-${c.id}`)}>Ladda kategori</button>
                        )}
                      </div>
                    </div>
                    {c.show_products && c.products ? (
                      <ul>
                        {c.products.map(p => (
                          <li>
                            <div class="hemfixarna_categories--label hemfixarna_product--label">
                              <div>
                                <p>{p.fields.title}</p>
                                <span>{p.fields.ID}</span>
                              </div>
                              <div>
                                <button onClick={() => navigator.clipboard.writeText(String(p.fields.ID))}>Kopiera ID</button>
                                {this.customer.at_hemfixarna ? (
                                  <button onClick={() => navigator.clipboard.writeText(`${getApiUrl()}/partner/${this.customer.post_name}?id=${p.fields.ID}`)}>Kopiera URL</button>
                                ) : (
                                  <button onClick={() => (this.selectedID = String(p.fields.ID))}>Ladda produkt</button>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul>
                        {c.sub_categories &&
                          c.sub_categories.map(sc => (
                            <li>
                              <div class="hemfixarna_categories--label">
                                <div>
                                  <p>{sc.name}</p>
                                  <span>{`c-${sc.id}`}</span>
                                </div>
                                <div>
                                  <button onClick={() => navigator.clipboard.writeText(`c-${sc.id}`)}>Kopiera ID</button>
                                  {this.customer.at_hemfixarna ? (
                                    <button onClick={() => navigator.clipboard.writeText(`${getApiUrl()}/partner/${this.customer.post_name}?id=c-${c.id}`)}>Kopiera URL</button>
                                  ) : (
                                    <button onClick={() => (this.selectedID = `c-${sc.id}`)}>Ladda kategori</button>
                                  )}
                                </div>
                              </div>
                              <ul>
                                {sc.products.map(p => (
                                  <li>
                                    <div class="hemfixarna_categories--label hemfixarna_product--label">
                                      <div>
                                        <p>{p.fields.title}</p>
                                        <span>{p.fields.ID}</span>
                                      </div>
                                      <div>
                                        <button onClick={() => navigator.clipboard.writeText(String(p.fields.ID))}>Kopiera ID</button>
                                        {this.customer.at_hemfixarna ? (
                                          <button onClick={() => navigator.clipboard.writeText(`${getApiUrl()}/partner/${this.customer.post_name}?id=${p.fields.ID}`)}>
                                            Kopiera URL
                                          </button>
                                        ) : (
                                          <button onClick={() => (this.selectedID = String(p.fields.ID))}>Ladda produkt</button>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                      </ul>
                    )}
                  </li>
                ))}
              </div>
            </ul>
          </div>
        ) : (
          <div>
            <form
              onSubmit={e => {
                e.preventDefault();
                this.handleLogin();
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                maxWidth: '300px',
                margin: 'auto',
                padding: '20px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                gridColumn: 'span 2',
              }}
            >
              <input type="password" placeholder="Lösenord" />
              <button type="submit">Logga in</button>
              {this.loginError && <span>Fel lösenord</span>}
            </form>
          </div>
        )}
      </Fragment>
    ) : (
      <h1
        style={{
          textAlign: 'center',
          margin: 'auto',
          padding: '20px',
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'red',
        }}
      >
        No customer found
      </h1>
    );
  }
}
